import type { StepView } from "@/app/queries";
import { Link } from "@tanstack/react-router";
import { Button } from "@/ui/Button";
import { useAssignmentByStep } from "@/app/notebookQueries";
import { ProgressBar } from "@/ui/Progress";

/** Правая панель S4: «до гейта осталось», «домашка шага» (только если у
 *  шага есть ноутбук), «параллельно». Все доли — из оценки гейта. */
/** Кнопка «открыть ноутбук»: задание шага ищется по шагу. */
function OpenNotebook({ stepId }: { stepId: string }) {
  const asg = useAssignmentByStep(stepId);
  if (asg.data)
    return (
      <Link to="/notebook/$assignmentId" params={{ assignmentId: asg.data.id }} className="no-underline">
        <Button variant="primary" className="w-full justify-center" tabIndex={-1}>
          открыть ноутбук
        </Button>
      </Link>
    );
  return (
    <Button className="w-full justify-center" disabled reason={asg.isPending ? "ищем ноутбук…" : "админ ещё не загрузил ноутбук-шаблон"}>
      открыть ноутбук
    </Button>
  );
}

export function StepAside({ view }: { view: StepView }) {
  const g = view.gate;
  const c = view.content;
  const bankMet = g.criteria.some((cr) => cr.kind === "question_bank" && cr.met);
  const hw = c.homework.file_blob_id ? c.files.find((f) => f.blob_id === c.homework.file_blob_id) : undefined;
  return (
    <div className="flex flex-col gap-3.5">
      <section className="rounded-md border border-bd2 bg-card p-4" aria-labelledby="aside-remaining">
        <h2 id="aside-remaining" className="m-0 mb-3 font-mono text-[11px] font-normal uppercase tracking-[.06em] text-mut">
          до гейта осталось
        </h2>
        <div className="flex flex-col gap-3">
          <ProgressBar label="юниты ресурсов" done={g.units_done} total={g.units_total} />
          <ProgressBar label="практика" done={g.tasks_done} total={g.tasks_total} />
          <div>
            <div className="mb-1.5 flex justify-between gap-2 font-mono text-[11px]">
              <span className="text-ink2">банк вопросов «{c.gate.bank.name}»</span>
              <span className={bankMet ? "text-ok" : "text-warn"}>{bankMet ? "пройден" : "не пройден"}</span>
            </div>
            <div className="h-1.5 overflow-hidden rounded-[3px] bg-card2" role="progressbar" aria-valuenow={bankMet ? 1 : 0} aria-valuemax={1} aria-label="банк вопросов">
              <div className="h-full" style={{ width: bankMet ? "100%" : "0%", background: bankMet ? "var(--ok)" : "var(--warn)" }} />
            </div>
          </div>
        </div>
        <p className="m-0 mt-3.5 text-pretty text-[12px] leading-[1.5] text-mut">Отметки сохраняются сразу. Прогресс — это гейт, а не доля просмотренного видео.</p>
      </section>

      {hw && (
        <section className="rounded-md border border-bd bg-card p-3.5" aria-labelledby="aside-hw">
          <h2 id="aside-hw" className="m-0 mb-2.5 font-mono text-[11px] font-normal text-mut">
            домашка шага
          </h2>
          <div className="mb-1.5 font-mono text-[12.5px]">{hw.name}</div>
          <div className="font-mono text-[11px] text-mut">{Math.round(hw.size / 1024)} КБ</div>
          <div className="mt-3">
            <OpenNotebook stepId={view.id} />
          </div>
        </section>
      )}

      {c.deps.parallel && (
        <section className="rounded-md border border-bd bg-card p-3.5" aria-labelledby="aside-par">
          <h2 id="aside-par" className="m-0 mb-2.5 font-mono text-[11px] font-normal text-mut">
            параллельно
          </h2>
          <div className="flex items-center gap-2.5">
            <span aria-hidden className="font-mono text-[11px] text-acc">
              ▸
            </span>
            <span className="flex-1 text-[12.5px]">{c.deps.parallel}</span>
          </div>
          <p className="m-0 mt-2 text-[12px] leading-[1.5] text-mut">Один алгоритмический шаг на один шаг маршрута.</p>
        </section>
      )}
    </div>
  );
}
